import { TableRowSideSheetForm } from "../../../../common/tables/view/TableRowSideSheetForm";
import React, {
    ChangeEvent,
    FC,
    PropsWithChildren,
    useCallback,
    useMemo,
} from "react";
import { PayoutRule, CostPayoutModifier } from "../../../../../../common/models";
import { PLPRCreateInput } from "../../../../../../common/inputs/ProductListingToPayoutRuleInput";
import {
    Button,
    Heading,
    PlusIcon,
    Portal,
    SelectField,
    TextInputField,
} from "evergreen-ui";
import { DateTimeInputField } from "../../../../common/DateTimeInputField";
import { PayoutRuleType } from "../../../../../../common/enums";
import { toTitleCase } from "../../../../../../common/utils";

interface AddAndAssignInput {
    // PayoutRule fields
    name?: string;
    type?: keyof typeof PayoutRuleType;
    retailPricePercentApplicable?: number;
    unitCostPercentApplicable?: number;
    juniperServiceFeePercentApplicable?: number;
    fulfillmentFeePercentApplicable?: number;
    processingFeePercentApplicable?: number;
    shippingSubsidyPercentApplicable?: number;
    vendorProfitSplitPercent?: number;
    // PLPR assignment fields
    startDate?: string;
    startThreshold?: number;
    costPayoutModifierId?: string | null;
}

interface Props {
    isShown: boolean;
    input: AddAndAssignInput;
    costPayoutModifiers: CostPayoutModifier[];
    mutationLoading: boolean;
    mutationCalled: boolean;
    mutationError: boolean;
    onInputChanged: (input: AddAndAssignInput) => void;
    onDismiss: () => void;
    onOpen: () => void;
    onSubmit: () => void;
}

export type { AddAndAssignInput };

export const AddAndAssignPayoutRuleForm: FC<Props> = ({
    isShown,
    input,
    costPayoutModifiers,
    mutationLoading,
    mutationCalled,
    mutationError,
    onInputChanged,
    onOpen,
    onDismiss,
    onSubmit,
}: PropsWithChildren<Props>) => {

    const onChange = ({ target: { value, name, type }}: ChangeEvent<HTMLInputElement>) => {
        onInputChanged({
            ...input,
            [name]: (type === "number") ? (value === "" ? undefined : parseFloat(value)) : value,
        });
    };

    const onSelectChange = ({ target: { value, name }}: ChangeEvent<HTMLSelectElement>) => {
        onInputChanged({
            ...input,
            [name]: value || undefined,
        });
    };

    const isReadyToSubmit = useMemo((): boolean => {
        return !!(
            input.name &&
            input.type &&
            input.retailPricePercentApplicable !== undefined &&
            input.unitCostPercentApplicable !== undefined &&
            input.juniperServiceFeePercentApplicable !== undefined &&
            input.fulfillmentFeePercentApplicable !== undefined &&
            input.processingFeePercentApplicable !== undefined &&
            input.shippingSubsidyPercentApplicable !== undefined &&
            input.vendorProfitSplitPercent !== undefined &&
            input.startDate
        );
    }, [input]);

    return (
        <>
            <Portal>
                <Button
                    minWidth={"160px"}
                    iconBefore={PlusIcon}
                    size={"large"}
                    appearance={"primary"}
                    position={"absolute"}
                    bottom={30}
                    right={90}
                    onClick={onOpen}
                    boxShadow={"0 4px 8px rgba(0, 0, 0, 0.2)"}
                >
                    Add Payout Rule
                </Button>
            </Portal>
            <TableRowSideSheetForm
                isShown={isShown}
                headerTitle={"Add & Assign Payout Rule"}
                mutationLoading={mutationLoading}
                mutationCalled={mutationCalled}
                mutationError={mutationError}
                canSubmit={isReadyToSubmit}
                onDismiss={onDismiss}
                onSubmit={onSubmit}
            >
                <Heading size={400} marginBottom={12}>Payout Rule</Heading>
                <TextInputField
                    name={"name"}
                    label={"Name"}
                    value={input.name ?? ""}
                    required={true}
                    maxWidth={"300px"}
                    isInvalid={!input.name}
                    onChange={onChange}
                />
                <SelectField
                    required={true}
                    name={"type"}
                    label={"Type"}
                    width={"100%"}
                    isInvalid={input.type === undefined}
                    value={input.type}
                    onChange={onSelectChange}
                >
                    {Object.values(PayoutRuleType).map((payoutRuleType, index) => (
                        <option key={index} value={payoutRuleType}>
                            {toTitleCase(payoutRuleType)}
                        </option>
                    ))}
                </SelectField>
                <TextInputField
                    name={"retailPricePercentApplicable"}
                    label={"Retail Price Percent Applicable"}
                    type={"number"}
                    value={input.retailPricePercentApplicable ?? ""}
                    isInvalid={input.retailPricePercentApplicable === undefined}
                    required={true}
                    onChange={onChange}
                />
                <TextInputField
                    name={"unitCostPercentApplicable"}
                    label={"Unit Cost Percent Applicable"}
                    type={"number"}
                    value={input.unitCostPercentApplicable ?? ""}
                    isInvalid={input.unitCostPercentApplicable === undefined}
                    required={true}
                    onChange={onChange}
                />
                <TextInputField
                    name={"juniperServiceFeePercentApplicable"}
                    label={"Juniper Service Fee Percent Applicable"}
                    type={"number"}
                    value={input.juniperServiceFeePercentApplicable ?? ""}
                    isInvalid={input.juniperServiceFeePercentApplicable === undefined}
                    required={true}
                    onChange={onChange}
                />
                <TextInputField
                    name={"fulfillmentFeePercentApplicable"}
                    label={"Fulfillment Fee Percent Applicable"}
                    type={"number"}
                    value={input.fulfillmentFeePercentApplicable ?? ""}
                    isInvalid={input.fulfillmentFeePercentApplicable === undefined}
                    required={true}
                    onChange={onChange}
                />
                <TextInputField
                    name={"processingFeePercentApplicable"}
                    label={"Processing Fee Percent Applicable"}
                    type={"number"}
                    value={input.processingFeePercentApplicable ?? ""}
                    isInvalid={input.processingFeePercentApplicable === undefined}
                    required={true}
                    onChange={onChange}
                />
                <TextInputField
                    name={"shippingSubsidyPercentApplicable"}
                    label={"Shipping Subsidy Percent Applicable"}
                    type={"number"}
                    value={input.shippingSubsidyPercentApplicable ?? ""}
                    isInvalid={input.shippingSubsidyPercentApplicable === undefined}
                    required={true}
                    onChange={onChange}
                />
                <TextInputField
                    name={"vendorProfitSplitPercent"}
                    label={"Vendor Profit Split"}
                    type={"number"}
                    value={input.vendorProfitSplitPercent ?? ""}
                    isInvalid={input.vendorProfitSplitPercent === undefined}
                    required={true}
                    onChange={onChange}
                />

                <Heading size={400} marginTop={24} marginBottom={12}>Assignment</Heading>
                <DateTimeInputField
                    label={"Start Date"}
                    value={input.startDate ?? null}
                    required={true}
                    maxWidth={"300px"}
                    isInvalid={!input.startDate}
                    onChange={(date) => {
                        onInputChanged({
                            ...input,
                            startDate: date?.toISOString() ?? undefined,
                        });
                    }}
                />
                <TextInputField
                    name={"startThreshold"}
                    label={"Start Threshold"}
                    type={"number"}
                    value={input.startThreshold ?? ""}
                    required={false}
                    onChange={onChange}
                />
                <SelectField
                    name={"costPayoutModifierId"}
                    label={"Cost Payout Modifier"}
                    value={input.costPayoutModifierId ?? ""}
                    onChange={onSelectChange}
                >
                    <option value={""}>None</option>
                    {costPayoutModifiers.map(cpm => (
                        <option key={cpm.id} value={cpm.id}>
                            {cpm.name}
                        </option>
                    ))}
                </SelectField>
            </TableRowSideSheetForm>
        </>
    );
};
